import { Center, Loader, ScrollArea, Skeleton } from "@mantine/core";
import { useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  getListTemplatesQueryKey,
  listTemplates,
  useGetTemplate,
  useListTemplateCategories,
} from "../../api/generated/endpoints/generation/generation";
import type {
  ListTemplatesParams,
  TemplateCard as Template,
  TemplateCategory,
  TemplateDetail,
  TemplatePage,
} from "../../api/generated/model";
import { freshUrls, soonestExpiry } from "../../api/signed-urls";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { EmptyState } from "../../ui/EmptyState";
import { LoadAhead, morePages } from "../../ui/LoadAhead";
import { SubmitSearch } from "../../ui/SubmitSearch";
import { TypeTabs } from "../../ui/TypeTabs";
import { TemplateCard } from "./TemplateCard";
import { TemplateDialog } from "./TemplateDialog";
import classes from "./templates.module.css";

/** Live's page of cards (24, as it scrolls) and its search's limit, the route's. */
const PAGE = 24;
const SEARCH_MAX = 100;
/** The All pill: no category sent. */
const ALL = "all";

type Pages = InfiniteData<TemplatePage, string | undefined>;

/**
 * Live's Library tab (`tabs/library`, captures 01 and 43): the search, applied on Enter or its
 * arrow; All and the categories that hold a card, one at a time, those that do not fit under
 * More; then the cards in live's order, its newest first, 24 a page, the next
 * as the end shows (live's `InfiniteScroll`, `ui/LoadAhead`). A card opens the template's preview, whose Use and
 * Replicate hand the template to the page (`onSelect`). A card's cover and examples are signed,
 * so the pages are read again before their URLs expire.
 */
export function TemplatesLibrary({
  teamId,
  onSelect,
  linked,
  onLinkDone,
}: {
  teamId: string;
  onSelect: (template: Template, replicate: boolean) => void;
  /** A template the page's link names (`?template=`): its preview opens once it is read, unless
   *  it is coming soon, which opens nothing, as its card. */
  linked?: string | undefined;
  /** The link's preview closed or handed its template on: the page drops the link. */
  onLinkDone?: (() => void) | undefined;
}) {
  const [previewed, setPreviewed] = useState<Template | null>(null);
  // The link's template, read by its id: its detail is a card and more, so the preview shows it
  // while the link is there, unless Explore more chose another.
  // Its examples are signed, so it is read again before they expire, as the list's covers are.
  const link = useGetTemplate(teamId, linked ?? "", {
    query: { enabled: linked !== undefined, ...freshUrls<TemplateDetail>((detail) => detail.urls_expire_at) },
  });
  // A coming soon template opens nothing, as its card is disabled: the link is dropped instead.
  const opens = linked !== undefined && link.data !== undefined && !link.data.coming_soon;
  const dropped = linked !== undefined && link.data?.coming_soon === true;
  useEffect(() => {
    if (dropped) onLinkDone?.();
  }, [dropped, onLinkDone]);
  const shown = previewed ?? (opens ? (link.data ?? null) : null);
  const closePreview = () => {
    setPreviewed(null);
    if (linked !== undefined) onLinkDone?.();
  };
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<TemplateCategory | null>(null);
  const categories = useListTemplateCategories(teamId);
  const params: ListTemplatesParams = {
    ...(search === "" ? {} : { search }),
    ...(category === null ? {} : { category }),
  };
  const templates = useInfiniteQuery({
    queryKey: [...getListTemplatesQueryKey(teamId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listTemplates(
        teamId,
        { ...params, limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    ...freshUrls<Pages>((data) => soonestExpiry(data.pages.map((page) => page.urls_expire_at))),
  });
  const items = templates.data?.pages.flatMap((page) => page.items) ?? [];
  const failed = templates.error ?? categories.error ?? (linked === undefined ? null : link.error);
  return (
    <div className={classes.library}>
      <div className={classes.toolbar}>
        <SubmitSearch
          label="Search by templates name"
          value={search}
          onSearch={setSearch}
          maxLength={SEARCH_MAX}
          className={classes.search}
        />
        {categories.data === undefined ? (
          <Skeleton className={classes.pillsLoading} aria-label="Loading the categories" />
        ) : (
          <TypeTabs
            fold
            label="Categories"
            value={category ?? ALL}
            onChange={(next) => setCategory(next === ALL ? null : (next as TemplateCategory))}
            tabs={[
              { value: ALL, label: "All" },
              ...categories.data.categories.map((each) => ({ value: each, label: each })),
            ]}
          />
        )}
      </div>
      {failed === null ? null : <ErrorAlert error={failed} />}
      {templates.isPending ? (
        <Center className={classes.loading}>
          <Loader aria-label="Loading the templates" />
        </Center>
      ) : (
        <ScrollArea type="auto" className={classes.scroll}>
          <div className={classes.scrollContent}>
            {items.length === 0 && templates.isSuccess ? (
              <EmptyState
                title="No templates found"
                description="Try a different search or category."
              />
            ) : (
              <ul className={classes.grid} aria-label="Templates">
                {items.map((template) => (
                  <li key={template.id}>
                    <TemplateCard template={template} onOpen={() => setPreviewed(template)} />
                  </li>
                ))}
              </ul>
            )}
            {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
            <LoadAhead more={morePages(templates)} loaded={items.length} label="Loading more templates" />
          </div>
        </ScrollArea>
      )}
      <TemplateDialog
        teamId={teamId}
        template={shown}
        onPreview={setPreviewed}
        onSelect={(template, replicate) => {
          closePreview();
          onSelect(template, replicate);
        }}
        onClose={closePreview}
      />
    </div>
  );
}
